// ─── Auth context ──────────────────────────────────────────────────────────────

import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAuthStore } from '../store/authStore';
import type { User, LoginCredentials } from '../types';
import type { LoginOutcome } from '../api/authService';
import { queryClient } from '../api/queryClient';
import { startMessageHub, stopMessageHub } from '../services/realtime/messageHub';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<LoginOutcome>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, isLoading, error, login, logout, clearError } = useAuthStore();

  // Bind the SignalR message hub to the auth lifecycle so incoming messages hit the
  // react-query cache the moment a user is authenticated, and the socket is torn
  // down cleanly on logout. Idempotent — startMessageHub no-ops if already open.
  useEffect(() => {
    if (isAuthenticated) {
      void startMessageHub(queryClient);
      return () => { void stopMessageHub(); };
    }
    return undefined;
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, error, login, logout, clearError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
